import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, ComposedChart, Area, Legend } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Building } from 'lucide-react';

interface DashboardProps {
  data: {
    summary: {
      totalRevenue: number;
      totalAdminFees: number;
      totalCommissions: number;
      totalTalarRevenue: number;
      totalVendors: number;
      activeVendors: number;
      totalCustomers: number;
      revenueCustomers: number;
      totalTransactions: number;
      revenueTransactions: number;
    };
    vendors: Array<{
      name: string;
      revenue: number;
      admin_fees: number;
      commissions: number;
      talar_revenue: number;
      transactions: number;
      revenue_transactions: number;
      growth: number;
      customers: number;
      status: string;
    }>;
    monthlyRevenue: Array<{
      month: string;
      revenue: number;
      admin_fees: number;
      commissions: number;
      talar_revenue: number;
      transactions: number;
    }>;
    vendorMonthlyRevenue: Record<string, Array<{
      month: string;
      revenue: number;
      admin_fees: number;
      commissions: number;
      talar_revenue: number;
      transactions: number;
    }>>;
    topCustomers: Array<{
      name: string;
      revenue: number;
      transactions: number;
    }>;
    vendorTopCustomers: Record<string, Array<{
      name: string;
      revenue: number;
      transactions: number;
    }>>;
    recentTransactions: Array<{
      id: string;
      vendor: string;
      customer: string;
      amount: number;
      admin_fee: number;
      commission: number;
      date: string;
      product: string;
      status: string;
    }>;
    vendorTransactions: Record<string, Array<{
      id: string;
      vendor: string;
      customer: string;
      amount: number;
      admin_fee: number;
      commission: number;
      date: string;
      product: string;
      status: string;
    }>>;
    products: Array<{
      name: string;
      vendor: string;
      gross_sales: number;
      talar_revenue: number;
      margin_pct: number;
      quantity: number;
      transactions: number;
    }>;
    forecast: {
      monthly: Array<{
        month: string;
        month_short: string;
        revenue: number;
        talar: number;
        revenue_low: number;
        revenue_high: number;
        talar_low: number;
        talar_high: number;
      }>;
      quarterly: Array<{
        quarter: string;
        revenue: number;
        talar: number;
        revenue_low: number;
        revenue_high: number;
        talar_low: number;
        talar_high: number;
      }>;
      annual: {
        revenue_2025_actual: number;
        revenue_2026_base: number;
        revenue_2026_low: number;
        revenue_2026_high: number;
        talar_2025_actual: number;
        talar_2026_base: number;
        talar_2026_low: number;
        talar_2026_high: number;
        revenue_growth_pct: number;
        talar_growth_pct: number;
      };
      byVendor: Array<{
        vendor: string;
        revenue_2025: number;
        revenue_2026: number;
        talar_2025: number;
        talar_2026: number;
        growth_pct: number;
      }>;
      actuals2025: Array<{
        month: string;
        revenue: number | null;
        talar: number | null;
      }>;
      methodology: string;
      dataMonthsCount: number;
    };
  };
  userInfo?: any;
  onLogout?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ data, userInfo, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [transactionPage, setTransactionPage] = useState(1);
  const transactionsPerPage = 20;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Filter data based on selected filters
  const filteredVendors = selectedVendor === 'all' 
    ? data.vendors 
    : data.vendors.filter(v => v.name === selectedVendor);
  
  // Use per-vendor transactions when a vendor is selected
  const filteredTransactions = selectedVendor === 'all'
    ? data.recentTransactions
    : (data.vendorTransactions[selectedVendor] ?? []);

  // Pagination for transactions
  const totalTransactionPages = Math.ceil(filteredTransactions.length / transactionsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (transactionPage - 1) * transactionsPerPage,
    transactionPage * transactionsPerPage
  );

  // Use per-vendor top customers when a vendor is selected
  const filteredCustomers = selectedVendor === 'all'
    ? data.topCustomers
    : (data.vendorTopCustomers[selectedVendor] ?? []);

  // Filter products by vendor
  const filteredProducts = selectedVendor === 'all'
    ? data.products
    : data.products.filter(p => p.vendor === selectedVendor);

  // Use per-vendor monthly data when a vendor is selected
  const baseMonthlyRevenue = selectedVendor !== 'all' && data.vendorMonthlyRevenue[selectedVendor]
    ? data.vendorMonthlyRevenue[selectedVendor]
    : data.monthlyRevenue;

  // Filter monthly revenue based on selected period
  const filteredMonthlyRevenue = selectedPeriod === 'all'
    ? baseMonthlyRevenue
    : selectedPeriod === 'ytd'
    ? baseMonthlyRevenue
    : selectedPeriod === 'q1'
    ? baseMonthlyRevenue.filter(m => ['Jan', 'Feb', 'Mar'].includes(m.month))
    : selectedPeriod === 'q4'
    ? baseMonthlyRevenue.filter(m => ['Oct', 'Nov', 'Dec'].includes(m.month))
    : baseMonthlyRevenue;

  // Debug logging
  console.log('Dashboard Debug:', {
    selectedPeriod,
    selectedVendor,
    originalDataLength: data.monthlyRevenue.length,
    filteredDataLength: filteredMonthlyRevenue.length,
    filteredMonths: filteredMonthlyRevenue.map(m => m.month)
  });

  // Calculate period-based summary first
  const getPeriodSummary = () => {
    if (selectedPeriod === 'all') return data.summary;

    const periodRevenue = filteredMonthlyRevenue.reduce((sum, month) => sum + month.revenue, 0);
    const periodAdminFees = filteredMonthlyRevenue.reduce((sum, month) => sum + month.admin_fees, 0);
    const periodCommissions = filteredMonthlyRevenue.reduce((sum, month) => sum + month.commissions, 0);
    const periodTransactions = filteredMonthlyRevenue.reduce((sum, month) => sum + month.transactions, 0);

    return {
      ...data.summary,
      totalRevenue: periodRevenue,
      totalAdminFees: periodAdminFees,
      totalCommissions: periodCommissions,
      totalTalarRevenue: periodAdminFees + periodCommissions,
      totalTransactions: periodTransactions,
      revenueTransactions: Math.round(periodTransactions * 0.043),
    };
  };

  // Calculate filtered summary metrics (vendor + period)
  const getFilteredSummary = () => {
    let baseSummary = getPeriodSummary();

    if (selectedVendor === 'all') return baseSummary;

    const vendorData = data.vendors.find(v => v.name === selectedVendor);
    if (!vendorData) return baseSummary;

    // If specific vendor is selected, use vendor-specific data
    return {
      ...baseSummary,
      totalRevenue: vendorData.revenue,
      totalAdminFees: vendorData.admin_fees,
      totalCommissions: vendorData.commissions,
      totalTalarRevenue: vendorData.talar_revenue,
      totalTransactions: vendorData.transactions,
      revenueTransactions: vendorData.revenue_transactions,
      totalCustomers: vendorData.customers,
      revenueCustomers: vendorData.customers,
    };
  };

  const displaySummary = getFilteredSummary();


  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'vendors', label: 'Vendor Performance' },
    { id: 'customers', label: 'Customer Analytics' },
    { id: 'products', label: 'Products' },
    { id: 'transactions', label: 'Transaction Feed' },
    { id: 'forecast', label: '2026 Forecast' },
  ];

  const renderOverviewTab = () => (
    <div>
      {/* Scorecards */}
      <div className="scorecards-grid">
        <div className="scorecard">
          <div className="scorecard-header">
            <span className="scorecard-title">Total Vendor Revenue</span>
            <DollarSign className="scorecard-icon" />
          </div>
          <div className="scorecard-value">{formatCurrency(displaySummary.totalRevenue)}</div>
          <div className="scorecard-change positive">
            <TrendingUp size={16} />
            <span>Gross sales across all vendors</span>
          </div>
        </div>

        <div className="scorecard">
          <div className="scorecard-header">
            <span className="scorecard-title">Talar Earnings</span>
            <DollarSign className="scorecard-icon" />
          </div>
          <div className="scorecard-value">{formatCurrency(displaySummary.totalTalarRevenue)}</div>
          <div className="scorecard-change positive">
            <TrendingUp size={16} />
            <span>Admin fees &amp; commissions earned</span>
          </div>
        </div>

        <div className="scorecard">
          <div className="scorecard-header">
            <span className="scorecard-title">Talar Margin</span>
            <DollarSign className="scorecard-icon" />
          </div>
          <div className="scorecard-value">
            {displaySummary.totalRevenue > 0
              ? ((displaySummary.totalTalarRevenue / displaySummary.totalRevenue) * 100).toFixed(1) + '%'
              : '0%'}
          </div>
          <div className="scorecard-change positive">
            <TrendingUp size={16} />
            <span>Earnings as % of vendor sales</span>
          </div>
        </div>

        <div className="scorecard">
          <div className="scorecard-header">
            <span className="scorecard-title">Active Vendors</span>
            <Building className="scorecard-icon" />
          </div>
          <div className="scorecard-value">{data.summary.activeVendors}/{data.summary.totalVendors}</div>
          <div className="scorecard-change positive">
            <TrendingUp size={16} />
            <span>{data.summary.totalVendors - data.summary.activeVendors} in pipeline</span>
          </div>
        </div>
      </div>

      {/* Monthly Trend Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Cumulative YTD Talar Earnings */}
        <div className="chart-container">
          <h3 className="chart-title">YTD Talar Earnings (Cumulative)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={(() => {
              let running = 0;
              return filteredMonthlyRevenue.map(m => {
                running += m.talar_revenue;
                return { month: m.month, ytd_talar: running };
              });
            })()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="month" stroke="#6c757d" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: any) => [formatCurrency(value), 'YTD Talar Earnings']}
                labelStyle={{ color: '#212529' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
              />
              <Line type="monotone" dataKey="ytd_talar" stroke="#212529" strokeWidth={3} dot={{ fill: '#212529', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Talar Earnings Bar */}
        <div className="chart-container">
          <h3 className="chart-title">Monthly Talar Earnings</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={filteredMonthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="month" stroke="#6c757d" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: any) => [formatCurrency(value), 'Talar Earnings']}
                labelStyle={{ color: '#212529' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
              />
              <Bar dataKey="talar_revenue" fill="#212529" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderVendorsTab = () => {
    const activeVendors = filteredVendors.filter(v => v.revenue > 0 || v.talar_revenue > 0);
    const vendorsSortedByTalar = [...activeVendors].sort((a, b) => b.talar_revenue - a.talar_revenue);
    const vendorsSortedByRevenue = [...activeVendors].sort((a, b) => b.revenue - a.revenue);

    return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Talar Earnings per vendor */}
        <div className="chart-container">
          <h3 className="chart-title">Talar Earnings by Vendor</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={vendorsSortedByTalar} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis type="number" stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" stroke="#6c757d" width={110} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: any) => [formatCurrency(value), 'Talar Earnings']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
              />
              <Bar dataKey="talar_revenue" fill="#212529" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Vendor Revenue */}
        <div className="chart-container">
          <h3 className="chart-title">Vendor Sales Volume</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={vendorsSortedByRevenue} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis type="number" stroke="#6c757d" tickFormatter={(v) => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" stroke="#6c757d" width={110} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: any) => [formatCurrency(value), 'Vendor Sales']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
              />
              <Bar dataKey="revenue" fill="#6c757d" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Status</th>
              <th>Talar Earnings</th>
              <th>Admin Fees</th>
              <th>Commissions</th>
              <th>Vendor Sales</th>
              <th>Growth</th>
              <th>Customers</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.filter(v => v.revenue > 0 || v.talar_revenue > 0).map((vendor) => (
              <tr key={vendor.name} style={{
                backgroundColor: selectedVendor === vendor.name ? '#f8f9fa' : 'transparent',
                fontWeight: selectedVendor === vendor.name ? 'bold' : 'normal'
              }}>
                <td><strong>{vendor.name}</strong></td>
                <td>
                  <span className={`status-badge ${
                    vendor.status === 'Active' ? 'status-completed' :
                    vendor.status === 'Pipeline' ? 'status-pending' : 'status-no-data'
                  }`}>
                    {vendor.status}
                  </span>
                </td>
                <td><strong>{formatCurrency(vendor.talar_revenue)}</strong></td>
                <td>{formatCurrency(vendor.admin_fees)}</td>
                <td>{formatCurrency(vendor.commissions)}</td>
                <td>{formatCurrency(vendor.revenue)}</td>
                <td>
                  <span className={`scorecard-change ${vendor.growth >= 0 ? 'positive' : 'negative'}`}>
                    {vendor.growth >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {vendor.growth > 0 ? '+' : ''}{vendor.growth}%
                  </span>
                </td>
                <td>{vendor.customers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  const renderCustomersTab = () => (
    <div>
      <div className="chart-container">
        <h3 className="chart-title">Top Customers by Revenue</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredCustomers}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            <XAxis dataKey="name" stroke="#6c757d" angle={-45} textAnchor="end" height={80} />
            <YAxis stroke="#6c757d" tickFormatter={(value) => `$${value/1000}K`} />
            <Tooltip 
              formatter={(value: any) => [formatCurrency(value), 'Revenue']}
              contentStyle={{ 
                backgroundColor: '#ffffff', 
                border: '1px solid #e9ecef',
                borderRadius: '4px'
              }}
            />
            <Bar dataKey="revenue" fill="#212529" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Revenue</th>
              <th>Transactions</th>
              <th>Avg. Order Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer.name}>
                <td><strong>{customer.name}</strong></td>
                <td>{formatCurrency(customer.revenue)}</td>
                <td>{formatNumber(customer.transactions)}</td>
                <td>{formatCurrency(customer.revenue / customer.transactions)}</td>
                <td>
                  <span className="status-badge status-completed">
                    Active
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTransactionsTab = () => (
    <div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Vendor</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Vendor Sales</th>
              <th>Admin Fee</th>
              <th>Commission</th>
              <th>Talar Earnings</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.map((transaction) => (
              <tr key={transaction.id}>
                <td><strong>{transaction.id}</strong></td>
                <td>{transaction.vendor}</td>
                <td>{transaction.customer}</td>
                <td>{transaction.product}</td>
                <td>{formatCurrency(transaction.amount)}</td>
                <td>{formatCurrency(transaction.admin_fee)}</td>
                <td>{formatCurrency(transaction.commission)}</td>
                <td><strong>{formatCurrency(transaction.admin_fee + transaction.commission)}</strong></td>
                <td>{new Date(transaction.date).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge ${transaction.status.toLowerCase() === 'completed' ? 'status-completed' : 'status-pending'}`}>
                    {transaction.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {totalTransactionPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '10px 0' 
          }}>
            <div>
              Showing {((transactionPage - 1) * transactionsPerPage) + 1}-{Math.min(transactionPage * transactionsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setTransactionPage(prev => Math.max(1, prev - 1))}
                disabled={transactionPage === 1}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #212529',
                  backgroundColor: transactionPage === 1 ? '#f8f9fa' : '#ffffff',
                  color: transactionPage === 1 ? '#6c757d' : '#212529',
                  borderRadius: '4px',
                  cursor: transactionPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              
              <span style={{ 
                padding: '8px 16px', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #e9ecef', 
                borderRadius: '4px' 
              }}>
                Page {transactionPage} of {totalTransactionPages}
              </span>
              
              <button
                onClick={() => setTransactionPage(prev => Math.min(totalTransactionPages, prev + 1))}
                disabled={transactionPage === totalTransactionPages}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #212529',
                  backgroundColor: transactionPage === totalTransactionPages ? '#f8f9fa' : '#ffffff',
                  color: transactionPage === totalTransactionPages ? '#6c757d' : '#212529',
                  borderRadius: '4px',
                  cursor: transactionPage === totalTransactionPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderProductsTab = () => {
    const top20 = filteredProducts.slice(0, 20);
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Top Products by Sales Volume */}
          <div className="chart-container">
            <h3 className="chart-title">Top Products by Sales Volume</h3>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={top20.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis type="number" stroke="#6c757d" tickFormatter={(v) => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" stroke="#6c757d" width={160} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: any) => [formatCurrency(value), 'Sales']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
                />
                <Bar dataKey="gross_sales" fill="#6c757d" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Products by Talar Earnings */}
          <div className="chart-container">
            <h3 className="chart-title">Top Products by Talar Earnings</h3>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={[...filteredProducts].sort((a, b) => b.talar_revenue - a.talar_revenue).slice(0, 10)}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis type="number" stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" stroke="#6c757d" width={160} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: any) => [formatCurrency(value), 'Talar Earnings']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }}
                />
                <Bar dataKey="talar_revenue" fill="#212529" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Vendor</th>
                <th>Sales Volume</th>
                <th>Talar Earnings</th>
                <th>Margin %</th>
                <th>Units Sold</th>
                <th>Transactions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product, i) => (
                <tr key={i}>
                  <td><strong>{product.name}</strong></td>
                  <td>{product.vendor}</td>
                  <td>{formatCurrency(product.gross_sales)}</td>
                  <td><strong>{formatCurrency(product.talar_revenue)}</strong></td>
                  <td>
                    <span style={{ color: product.margin_pct >= 3 ? '#28a745' : product.margin_pct > 0 ? '#fd7e14' : '#6c757d' }}>
                      {product.margin_pct.toFixed(1)}%
                    </span>
                  </td>
                  <td>{formatNumber(product.quantity)}</td>
                  <td>{product.transactions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderForecastTab = () => {
    const { forecast } = data;
    const a = forecast.annual;

    // Merge actuals2025 + monthly forecast into one array for comparison chart
    const comparisonData = forecast.monthly.map((fm, i) => ({
      month: fm.month_short,
      forecast_rev: fm.revenue,
      forecast_tal: fm.talar,
      forecast_low: fm.revenue_low,
      forecast_high: fm.revenue_high,
      actual_rev: forecast.actuals2025[i]?.revenue ?? null,
      actual_tal: forecast.actuals2025[i]?.talar ?? null,
    }));

    return (
      <div>
        {/* Annual summary scorecards */}
        <div className="scorecards-grid">
          <div className="scorecard">
            <div className="scorecard-header">
              <span className="scorecard-title">2026 Revenue Forecast</span>
              <TrendingUp className="scorecard-icon" />
            </div>
            <div className="scorecard-value">{formatCurrency(a.revenue_2026_base)}</div>
            <div className="scorecard-change positive">
              <TrendingUp size={16} />
              <span>+{a.revenue_growth_pct}% vs 2025 actual ({formatCurrency(a.revenue_2025_actual)})</span>
            </div>
          </div>

          <div className="scorecard">
            <div className="scorecard-header">
              <span className="scorecard-title">2026 Talar Earnings Forecast</span>
              <DollarSign className="scorecard-icon" />
            </div>
            <div className="scorecard-value">{formatCurrency(a.talar_2026_base)}</div>
            <div className="scorecard-change positive">
              <TrendingUp size={16} />
              <span>+{a.talar_growth_pct}% vs 2025 ({formatCurrency(a.talar_2025_actual)})</span>
            </div>
          </div>

          <div className="scorecard">
            <div className="scorecard-header">
              <span className="scorecard-title">Conservative Scenario</span>
              <TrendingDown className="scorecard-icon" />
            </div>
            <div className="scorecard-value">{formatCurrency(a.revenue_2026_low)}</div>
            <div className="scorecard-change" style={{ color: '#6c757d' }}>
              <span>Talar: {formatCurrency(a.talar_2026_low)}</span>
            </div>
          </div>

          <div className="scorecard">
            <div className="scorecard-header">
              <span className="scorecard-title">Optimistic Scenario</span>
              <TrendingUp className="scorecard-icon" />
            </div>
            <div className="scorecard-value">{formatCurrency(a.revenue_2026_high)}</div>
            <div className="scorecard-change positive">
              <TrendingUp size={16} />
              <span>Talar: {formatCurrency(a.talar_2026_high)}</span>
            </div>
          </div>
        </div>

        {/* Monthly comparison charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Revenue: 2025 actuals (dated) vs 2026 forecast */}
          <div className="chart-container">
            <h3 className="chart-title">Monthly Revenue — 2025 Actuals vs 2026 Forecast</h3>
            <p style={{ fontSize: '11px', color: '#6c757d', margin: '0 0 8px' }}>
              2025 actuals reflect dated transactions only (~{forecast.dataMonthsCount} months tracked). Shaded band = conservative–optimistic range.
            </p>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis dataKey="month" stroke="#6c757d" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any, name: any) => [formatCurrency(v), name]} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Area type="monotone" dataKey="forecast_high" fill="rgba(33,37,41,0.08)" stroke="none" name="Optimistic" legendType="none" />
                <Area type="monotone" dataKey="forecast_low"  fill="white"                stroke="none" name="Conservative" legendType="none" />
                <Line type="monotone" dataKey="forecast_rev" stroke="#212529" strokeWidth={2} dot={{ r: 3 }} name="2026 Forecast" />
                <Line type="monotone" dataKey="actual_rev"   stroke="#6c757d" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} name="2025 Actuals (dated)" connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Talar earnings: 2025 vs 2026 */}
          <div className="chart-container">
            <h3 className="chart-title">Monthly Talar Earnings — 2025 Actuals vs 2026 Forecast</h3>
            <p style={{ fontSize: '11px', color: '#6c757d', margin: '0 0 8px' }}>
              Shaded band = conservative–optimistic range.
            </p>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis dataKey="month" stroke="#6c757d" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6c757d" tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any, name: any) => [formatCurrency(v), name]} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Area type="monotone" dataKey="forecast_high" fill="rgba(33,37,41,0.08)" stroke="none" name="Optimistic" legendType="none" />
                <Area type="monotone" dataKey="forecast_low"  fill="white"                stroke="none" name="Conservative" legendType="none" />
                <Line type="monotone" dataKey="forecast_tal" stroke="#212529" strokeWidth={2} dot={{ r: 3 }} name="2026 Forecast" />
                <Line type="monotone" dataKey="actual_tal"   stroke="#6c757d" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} name="2025 Actuals (dated)" connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quarterly forecast bar chart */}
        <div className="chart-container">
          <h3 className="chart-title">2026 Quarterly Revenue Forecast</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={forecast.quarterly} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="quarter" stroke="#6c757d" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6c757d" tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any, name: any) => [formatCurrency(v), name]} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '4px' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="revenue_low"  fill="#e9ecef" name="Conservative" radius={[3,3,0,0]} />
              <Bar dataKey="revenue"      fill="#6c757d" name="Base"         radius={[3,3,0,0]} />
              <Bar dataKey="revenue_high" fill="#212529" name="Optimistic"   radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Vendor forecast table */}
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>2025 Revenue (Actual)</th>
                <th>2026 Revenue (Forecast)</th>
                <th>2025 Talar Earnings</th>
                <th>2026 Talar Earnings</th>
                <th>Growth %</th>
              </tr>
            </thead>
            <tbody>
              {forecast.byVendor.map((v) => (
                <tr key={v.vendor}>
                  <td><strong>{v.vendor}</strong></td>
                  <td>{formatCurrency(v.revenue_2025)}</td>
                  <td><strong>{formatCurrency(v.revenue_2026)}</strong></td>
                  <td>{formatCurrency(v.talar_2025)}</td>
                  <td><strong>{formatCurrency(v.talar_2026)}</strong></td>
                  <td>
                    <span style={{ color: v.growth_pct >= 10 ? '#28a745' : v.growth_pct >= 0 ? '#fd7e14' : '#dc3545', fontWeight: 'bold' }}>
                      +{v.growth_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: '11px', color: '#6c757d', marginTop: '12px', padding: '0 4px' }}>
            <strong>Methodology:</strong> {forecast.methodology}
          </p>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'vendors':
        return renderVendorsTab();
      case 'customers':
        return renderCustomersTab();
      case 'products':
        return renderProductsTab();
      case 'transactions':
        return renderTransactionsTab();
      case 'forecast':
        return renderForecastTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-top">
          {/* Left Section: Talar Medical Logo and Branding */}
          <div className="header-left-section">
            <img 
              src="/images/talar-medical-logo.svg"
              alt="Talar Medical Logo"
              style={{
                height: '60px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <div className="company-info">
              <h1 className="company-name" style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#000000',
                margin: '0 0 4px 0'
              }}>Talar Medical</h1>
              <p className="company-tagline" style={{
                fontSize: '16px',
                color: '#2196f3',
                margin: '0 0 8px 0'
              }}>Physician-Owned Group Purchasing Organization</p>
              <h2 className="dashboard-title" style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#000000',
                margin: '0'
              }}>Business Intelligence Dashboard</h2>
            </div>
          </div>

          {/* Right Section: User Info and Logout */}
          <div className="header-right-section">
            {userInfo && (
              <div className="user-info">
                <div className="user-details">
                  <span className="user-name">{userInfo.name}</span>
                  <span className="user-role">({userInfo.role})</span>
                </div>
                <button 
                  onClick={onLogout}
                  className="logout-button"
                  title="Sign Out"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
        <p className="dashboard-subtitle">
          {selectedVendor !== 'all' ? `Filtered by: ${selectedVendor} • ` : ''}
          {selectedPeriod !== 'all' ? `Period: ${selectedPeriod.toUpperCase()} • ` : ''}
          Live data: {displaySummary.revenueTransactions.toLocaleString()}/{displaySummary.totalTransactions.toLocaleString()} revenue transactions • ${displaySummary.totalRevenue.toLocaleString()} total revenue • {data.summary.activeVendors}/{data.summary.totalVendors} active vendors
        </p>
        <div className="dashboard-branding" style={{
          fontSize: '12px',
          color: '#666666',
          textAlign: 'center',
          marginTop: '10px'
        }}>
          © 2007-2025 Talar Medical, LLC. All Rights Reserved. | Powered By A2 Business Consulting 2026
        </div>
      </div>

      {/* Global Filters */}
      <div className="filters-container">
        <div className="filter-group">
          <label className="filter-label">Vendor</label>
          <select
            className="filter-select"
            value={selectedVendor}
            onChange={(e) => { setSelectedVendor(e.target.value); setTransactionPage(1); }}
          >
            <option value="all">All Vendors</option>
            {data.vendors.map(vendor => (
              <option key={vendor.name} value={vendor.name}>{vendor.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Period</label>
          <select
            className="filter-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="ytd">Year to Date</option>
            <option value="q1">Q1 2025</option>
            <option value="q4">Q4 2024</option>
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <ul className="tab-list">
          {tabs.map((tab) => (
            <li key={tab.id} className="tab-item">
              <button
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;